import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import ContactMailOutlined from '@mui/icons-material/ContactMailOutlined';

import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import PageSection from '../PageSection';
import { chartColours } from '../../../core/constants/colours.constants';

interface ParticipantInfoProps {
  address?: string | null;
  email?: string | null;
  telephone?: string | null;
  web?: string | null;
  version?: string;
}

const sectionNumbers = Array.from({ length: 22 }, (_, index) => index + 1);

const getTranslation = (version?: string) => {
  if (!version) return { file: 'pis_v5_2', PIS_VERSION: 'v5.2', PIS_DATE: '06 April 2023' };

  const num = version.match(/(\d+.\d+|\d+)/) || [0];

  if (num[0] >= '5') {
    return { file: 'pis_v5_2', PIS_VERSION: 'v5.2', PIS_DATE: '06 April 2023' };
  }

  if (num[0] >= '4') {
    return {
      file: 'pis_v4_2',
      PIS_VERSION: 'v4.2',
      PIS_DATE: '07 October 2022',
    };
  }

  if (version === 'v3.2') {
    return {
      file: 'pis_v3_2',
      PIS_VERSION: 'v3.2',
      PIS_DATE: '08 FEB 2022',
    };
  }

  if (version === 'v3.1') {
    return {
      file: 'pis_v3_1',
      PIS_VERSION: 'v3.1',
      PIS_DATE: '10 JAN 2022',
    };
  }

  return {
    file: 'pis_v3_0',
    PIS_VERSION: 'v3.0',
    PIS_DATE: '10 MAY 2020',
  };
};

function ParticipantInfo({
  address = null,
  email = null,
  telephone = null,
  web = null,
  version,
}: ParticipantInfoProps) {
  const { file, PIS_VERSION, PIS_DATE } = getTranslation(version);
  const { t } = useTranslation(file);

  return (
    <Box
      sx={{
        '& p': {
          lineHeight: 1.65,
        },
        '& ul, & ol': {
          pl: 3,
          my: 1.5,
        },
        '& li': {
          mb: 0.75,
          lineHeight: 1.6,
        },
        '& a': {
          fontWeight: 900,
        },
        '& strong': {
          fontWeight: 900,
        },
      }}
    >
      <PageSection
        title={t('title')}
        blurb={t('heading1')}
        colour={chartColours[0]}
        icon={<ArticleOutlined sx={{ fontSize: 28 }} />}
      >
        <ReactMarkdown>{t('intro')}</ReactMarkdown>
      </PageSection>

      {sectionNumbers.map((sectionNumber) => {
        const sectionColour = chartColours[(sectionNumber - 1) % chartColours.length];

        return (
          <PageSection
            title={t(`section${sectionNumber}.title`)}
            colour={sectionColour}
            icon={sectionNumber}
            key={`pis-section-${sectionNumber}`}
          >
            <ReactMarkdown>
              {t(`section${sectionNumber}.body`, {
                address,
                email,
                telephone,
                web,
              })}
            </ReactMarkdown>
          </PageSection>
        );
      })}

      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: `${chartColours[1]}55`,
          borderRadius: 3,
          bgcolor: `${chartColours[1]}12`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${chartColours[1]}26`,
              color: chartColours[1],
              border: 1,
              borderColor: `${chartColours[1]}55`,
              flexShrink: 0,
            }}
          >
            <ContactMailOutlined sx={{ fontSize: 24 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 800,
                mb: 0.5,
              }}
            >
              Discover Me South Africa
            </Typography>

            <Box
              sx={{
                '& p': {
                  m: 0,
                  fontWeight: 900,
                  lineHeight: 1.45,
                },
              }}
            >
              <span data-testid="pis-version">
                <ReactMarkdown>{`Discover Me South Africa | Participant information sheet | ${PIS_VERSION} | ${PIS_DATE}`}</ReactMarkdown>
              </span>
            </Box>
          </Box>

          <Chip
            label={PIS_VERSION}
            size="small"
            sx={{
              ml: 'auto',
              display: { xs: 'none', sm: 'inline-flex' },
              fontWeight: 900,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: `${chartColours[1]}55`,
              color: chartColours[1],
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantInfo;
